import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Form, Button, Image, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import accountBg from '../../../assets/images/account/accountBg.png';
import appIcon from '../../../assets/images/common/appIcon.png';

import '../login/login.css';

const Login = () => {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    db_key: '' // auto-filled from /main/checkadmin
  });
  const [emailError, setEmailError] = useState('');

  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const [forgotEmail, setForgotEmail] = useState('');
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);

  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    email: '',
    old_password: '',
    new_password: '',
    confirm_password: '',
  });

  // debounce so we don’t spam API while typing
  const emailDebounceRef = useRef(null);

 const cleanEmail = (s) => (s || '').trim();   // ✅ keeps exact case


  // ---------------- Email validation: /main/checkadmin ----------------
  const checkAdminByEmail = async (email) => {
    const e = cleanEmail(email);

    if (!e) {
      setIsEmailVerified(false);
      setFormData((p) => ({ ...p, db_key: '' }));
      setEmailError('');
      return false;
    }

    setCheckingEmail(true);
    try {
      const resp = await axios.get(`${baseUrl}/main/checkadmin`, {
        params: { email: e }
      });

      const dbKey = resp?.data?.[0]?.db_key || '';

      if (!dbKey || dbKey === 'USER NOT FOUND') {
        setIsEmailVerified(false);
        setFormData((p) => ({ ...p, db_key: '' }));
        setEmailError('User not found');
        return false;
      }

      setFormData((p) => ({ ...p, db_key: dbKey }));
      setIsEmailVerified(true);
      setEmailError('');
      return true;
    } catch (err) {
      console.error('checkadmin error:', err);
      setIsEmailVerified(false);
      setFormData((p) => ({ ...p, db_key: '' }));
      setEmailError('Unable to verify email. Please try again.');
      return false;
    } finally {
      setCheckingEmail(false);
    }
  };


  // Auto-check email after user stops typing (optional, smooth UX)
  useEffect(() => {
    if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);

    const e = cleanEmail(formData.email);
    if (!e) {
      setIsEmailVerified(false);
      setFormData((p) => ({ ...p, db_key: '' }));
      return;
    }

    emailDebounceRef.current = setTimeout(() => {
      checkAdminByEmail(e);
    }, 600);

    return () => {
      if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.email]);

  // ---------------- Handlers ----------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'email') {
      setFormData((p) => ({ ...p, email: value, db_key: '', password: '' }));
      setIsEmailVerified(false);
      setEmailError('');
      return;
    }


    setFormData((p) => ({ ...p, [name]: value }));
  };

  // ✅ Validate email when user tries to enter password (no verify button)
  const handlePasswordFocus = async () => {
    if (checkingEmail) return;

    // if already verified, don’t call again
    if (isEmailVerified && formData.db_key) return;

    if (!cleanEmail(formData.email)) {
      toast.error('Please enter email first.');
      return;
    }

    const ok = await checkAdminByEmail(formData.email);
    if (!ok) {
      toast.error('Email not found / not authorized');
    }
  };

  // Also validate on password click (some browsers)
  const handlePasswordMouseDown = async () => {
    await handlePasswordFocus();
  };

  // ---------------- Login submit ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();
  const email = cleanEmail(formData.email);

    const { password, db_key } = formData;

    if (!email) {
      toast.error('Please enter email.');
      return;
    }

    // ensure verified before login
    if (!isEmailVerified || !db_key) {
      const ok = await checkAdminByEmail(email);
      if (!ok) {
        toast.error('Email not found / not authorized');
        return;
      }
    }

    if (!password) {
      toast.error('Please enter password.');
      return;
    }

    setLoading(true);
    try {
      const payload = { email, password, db_key: formData.db_key };
      const response = await axios.post(`${baseUrl}/login/`, payload);

      if (response.data) {
        const {
          user_id,
          user_name,
          school_id,
          school_name,
          academic_year_id,
          academic_year_name,
          role_id,
          role_name,
          logo,
          branch,
          gender,
        } = response.data;

        sessionStorage.setItem(
          'user',
          JSON.stringify({
            user_id,
            user_name,
            school_id,
            school_name,
            academic_year_id,
            academic_year_name,
            role_id,
            role_name,
            logo,
            branch,
            gender,
            db_key: formData.db_key,
          })
        );

        if (role_name === 'Director' || role_name === 'Principal') {
          navigate('/principaldashboard');
        } else if (role_name === 'Teacher') {
          navigate('/dashboard');
        } else if (role_name?.trim()?.toLowerCase() === 'child future admin') {
          navigate('/finance/reports');
        } else {
          navigate('/principaldashboard');
        }
      } else {
        toast.error('Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Reset/Forgot password (same as your old logic) ----------------
  const handleResetPasswordChange = (e) => {
    setResetPasswordData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    const { email, old_password, new_password, confirm_password } = resetPasswordData;

    if (!email || !old_password || !new_password || !confirm_password) {
      toast.error('All fields are required.');
      return;
    }
    if (new_password !== confirm_password) {
      toast.error('New password and confirm password must match.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${baseUrl}/resetpassword/`, {
        email,
        current_password: old_password,
        new_password,
      });

      if (response.status === 200 && response.data.message === "Password reset successfully") {
        toast.success('Password reset successfully.');
        setShowResetPasswordModal(false);
        setResetPasswordData({
          email: '',
          old_password: '',
          new_password: '',
          confirm_password: '',
        });
      } else {
        toast.error(response.data?.detail || 'Failed to reset password.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const length = 10;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*!";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!forgotEmail) {
      toast.error('Please enter your email.');
      return;
    }

    const newPassword = generatePassword();
    setLoading(true);

    try {
      const checkEmailResponse = await axios.post(`${baseUrl}/check-email/`, {
        email: forgotEmail,
      });

      if (checkEmailResponse.status === 200 && checkEmailResponse.data.exists) {
        const forgotPasswordResponse = await axios.post(`${baseUrl}/forgotpassword/`, {
          email: forgotEmail,
          new_password: newPassword,
          password_flag: "Y",
        });

        if (forgotPasswordResponse.status === 200) {
          await axios.post(`${baseUrl}/send-email`, {
            email: forgotEmail,
            password: newPassword,
          });

          toast.success("A new password has been sent to your email.");
          setShowForgotPasswordModal(false);
          setForgotEmail('');
        } else {
          toast.error(forgotPasswordResponse.data.message || "Failed to reset password.");
        }
      } else {
        toast.error("Email not found.");
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- UI ----------------
  return (
    <div className='height100'>
      <ToastContainer />

      <Row className='height100'>
        <Col xs={12} md={6} lg={7} className='height100'>
          <div className='accountBg'>
            <div className='accounBgInner'>
              <Image src={accountBg} alt="Account Background" fluid rounded />
            </div>
          </div>
        </Col>

        <Col xs={12} md={6} lg={5} className='height100'>
          <div className='accountInner height100'>
            <div>
              <Row>
                <Col xs={12}>
                  <div className='accountLogoIcon'>
                    <Image src={appIcon} alt="App Icon" fluid rounded />
                  </div>
                  <h2>Welcome Back!</h2>
                  <p>Enter to get unlimited access to data & information.</p>
                </Col>
              </Row>
            </div>

            <div>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col xs={12}>
                    <div className='commonInput'>
                      <Form.Group controlId="email">
                        <Form.Label>Email <span className='requiredStar'>*</span></Form.Label>

                        <Form.Control
                          required
                          type="email"
                          name="email"
                          className="input"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Enter your Email Address"
                        />

                        {/* ✅ ADD THIS HERE (below the email input) */}
                        {emailError ? (
                          <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                            {emailError}
                          </div>
                        ) : null}

                        <div style={{ fontSize: 12, marginTop: 6 }}>
                          {checkingEmail ? (
                            <span className="text-muted">
                              <Spinner animation="border" size="sm" className="me-1" />
                              Checking email...
                            </span>
                          ) : isEmailVerified ? (
                            <span className="text-success">
                              <i className="bi bi-check-circle-fill me-1"></i>
                              Email verified
                            </span>
                          ) : (
                            <span className="text-muted">
                              Email will be validated when you enter password.
                            </span>
                          )}
                        </div>
                      </Form.Group>

                    </div>
                  </Col>

                  <Col sm={12}>
                    <div className='commonInput'>
                      <Form.Group controlId="password">
                        <Form.Label>Password <span className='requiredStar'>*</span></Form.Label>
                        <Form.Control
                          required
                          type="password"
                          name="password"
                          className="input"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Enter your Password"
                          onFocus={handlePasswordFocus}
                          onMouseDown={handlePasswordMouseDown}
                          disabled={checkingEmail}
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs="12" className='mb-3'>
                    <div className="floatR">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowForgotPasswordModal(true);
                        }}
                      >
                        Forgot Password?
                      </a>
                      <span style={{ margin: "0 10px" }}>|</span>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowResetPasswordModal(true);
                        }}
                      >
                        Reset Password
                      </a>
                    </div>
                  </Col>

                  <Col xs="12">
                    <Button
                      variant="primary"
                      className='submit w-100 commonBtn'
                      type="submit"
                      disabled={loading || checkingEmail}
                    >
                      {loading ? 'Logging in...' : 'Login'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </Col>
      </Row>

      {/* Forgot Password Modal */}
      <Modal show={showForgotPasswordModal} onHide={() => setShowForgotPasswordModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Forgot Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleForgotPassword}>
            <Form.Group controlId="forgotEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
              />
            </Form.Group>

            <div className="mt-4 d-flex justify-content-end">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Processing...' : 'Submit'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={showResetPasswordModal} onHide={() => setShowResetPasswordModal(false)} centered>
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="w-100 text-center">
            <i className="bi bi-shield-lock-fill me-2"></i> Reset Your Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleResetPasswordSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Email</Form.Label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-person-fill"></i>
                </span>
                <Form.Control
                  type="email"
                  name="email"
                  placeholder="Enter your registered email"
                  value={resetPasswordData.email}
                  onChange={handleResetPasswordChange}
                  className="py-2"
                  required
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Current Password</Form.Label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-key-fill me-2"></i>
                </span>
                <Form.Control
                  type="password"
                  name="old_password"
                  placeholder="Enter your current password"
                  value={resetPasswordData.old_password}
                  onChange={handleResetPasswordChange}
                  className="py-2"
                  required
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">New Password</Form.Label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-shield-lock-fill me-2"></i>
                </span>
                <Form.Control
                  type="password"
                  name="new_password"
                  placeholder="New password"
                  value={resetPasswordData.new_password}
                  onChange={handleResetPasswordChange}
                  className="py-2"
                  required
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">Confirm Password</Form.Label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-shield-lock-fill me-2"></i>
                </span>
                <Form.Control
                  type="password"
                  name="confirm_password"
                  placeholder="Confirm password"
                  value={resetPasswordData.confirm_password}
                  onChange={handleResetPasswordChange}
                  className="py-2"
                  required
                />
              </div>
            </Form.Group>

            <div className="d-flex justify-content-center">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? <i className="bi bi-arrow-repeat"></i> : null} Submit
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Login;
